// 수집 데이터 화면의 "시간대별" 차트들이 쓰는 순수 계산 로직만 모아 둔 파일.
// 컴포넌트(JSX)에 로직을 섞지 않아야 eqpHourly.test.js 로 따로 검증할 수 있다.
import dayjs from 'dayjs';

const MIN_MS = 60 * 1000;

// 스택 쌓는 순서 = 범례/툴팁에 보여줄 순서
export const HOURLY_STATE_KEYS = ['RUNNING', 'HOLD', 'ALARM', 'EMERGENCY', 'IDLE', 'OFFLINE', 'NODATA'];

// 실제 막대로 그리는 키 - NODATA 는 막대로 안 그린다(빈 시간이 회색 덩어리로 보이지 않게,
// 그냥 그 시간의 막대 높이가 낮게 끝나도록 둔다). 툴팁에는 여전히 NODATA 분을 보여준다.
export const BAR_STATE_KEYS = HOURLY_STATE_KEYS.filter((k) => k !== 'NODATA');

export const HOURLY_STATE_LABEL = {
  RUNNING: '운전',
  HOLD: '일시정지',
  ALARM: '알람',
  EMERGENCY: '비상정지',
  IDLE: '정지',
  OFFLINE: '오프라인',
  NODATA: '데이터 없음',
};

/**
 * 어떤 시간(bucket)에서 keys 순서상 가장 나중에(=스택에서 가장 위에) 그려지는,
 * 값이 0보다 큰 키를 찾는다. 막대 그래프에서 그 시간대 막대의 "맨 위 조각"에만
 * 둥근 모서리를 주기 위해 필요 - 조각마다 둥글게 하면 조각 사이에 배경색 틈이 생긴다.
 */
export const topmostBarKey = (bucket, keys = BAR_STATE_KEYS) => {
  for (let i = keys.length - 1; i >= 0; i -= 1) {
    if (bucket && bucket[keys[i]] > 0) return keys[i];
  }
  return null;
};

const toMs = (v) => dayjs(v).valueOf();

/**
 * 하루 집계를 끝낼 시각(ms) - 오늘이면 지금, 지난 날짜면 다음날 00:00.
 * timeline/metrics API 응답이 이미 "하루 범위 + 지금"으로 잘려서 오지만, STATE 구간이
 * edDt 없이(진행중) 내려올 때 그 구간을 어디서 끊을지도 이 값이 필요해서 따로 받는다.
 */
export const dayCutoffMs = (dayStart, isToday, now) => (
  isToday ? toMs(now) : dayjs(dayStart).add(1, 'day').valueOf()
);

/**
 * STATE·ALARM 구간을 시간(hour) 단위로 쪼개 각 시간대의 상태별 분(분, minute)을 구한다.
 *
 * 분류 규칙(한 시점은 분류 하나만 가진다 - 합이 항상 그 시간의 elapsedMin 과 같아야 함):
 *  - STATE 구간 값이 EMERGENCY 면 무조건 '비상정지'(EMERGENCY 로 분류한다 - ALARM 과 겹쳐도
 *    섞지 않는다. 둘 다 "알람류" 개념이라 혼동하기 쉬워서 일부러 분리해 둔다).
 *  - 그 외에 ALARM 구간(val=ON, 즉 "발생중")과 겹치면 '알람'(ALARM) 으로 분류한다 - 알람이 떠
 *    있는 동안의 기계 상태(운전중이든 정지든)보다 "알람이 떠 있었다"는 사실이 더 중요해서다.
 *  - 그 외엔 STATE 구간 값 그대로(RUNNING/HOLD/IDLE/OFFLINE).
 *  - STATE 구간이 비어 있는(값 자체가 없는) 시간은 '데이터 없음' - 단 cutoff(지금/하루끝)
 *    이후는 "아직 안 지난 시간"이라 채우지 않는다(그 시간 bucket 자체의 elapsedMin 이 0).
 *
 * @param intervals timeline.intervals (typ STATE|ALARM|PROGRAM, val, stDt, edDt)
 * @param dayStart  그 날짜 00:00 (dayjs 로 parse 가능한 값)
 * @param cutoffMs  집계를 끝낼 시각(ms) - dayCutoffMs() 결과
 * @returns 24개 { hour, elapsedMin, RUNNING, HOLD, ALARM, EMERGENCY, IDLE, OFFLINE, NODATA }
 */
export const computeHourlyStateStacks = (intervals, dayStart, cutoffMs) => {
  const dayStartMs = toMs(dayStart);

  const normalize = (list) => (list || [])
    .map((iv) => ({ st: toMs(iv.stDt), ed: iv.edDt ? toMs(iv.edDt) : cutoffMs, val: iv.val }))
    .filter((iv) => Number.isFinite(iv.st) && iv.ed > iv.st);

  const stateIntervals = normalize((intervals || []).filter((iv) => iv.typ === 'STATE'));
  const alarmOnIntervals = normalize((intervals || []).filter((iv) => iv.typ === 'ALARM' && iv.val === 'ON'));

  const stateValAt = (ms) => {
    const hit = stateIntervals.find((iv) => ms >= iv.st && ms < iv.ed);
    return hit ? hit.val : null;
  };
  const alarmOnAt = (ms) => alarmOnIntervals.some((iv) => ms >= iv.st && ms < iv.ed);

  const hours = [];
  for (let h = 0; h < 24; h += 1) {
    const hourStart = dayStartMs + h * 60 * MIN_MS;
    const hourEnd = hourStart + 60 * MIN_MS;
    const effectiveEnd = Math.min(hourEnd, cutoffMs);
    const elapsedMs = Math.max(0, effectiveEnd - hourStart);

    const bucket = {
      hour: h,
      elapsedMin: Math.round(elapsedMs / MIN_MS),
      RUNNING: 0, HOLD: 0, ALARM: 0, EMERGENCY: 0, IDLE: 0, OFFLINE: 0, NODATA: 0,
    };
    hours.push(bucket);
    if (elapsedMs <= 0) continue; // 아직 안 지난 시간(미래) - 전부 0, '데이터 없음'도 아님

    // 분류가 바뀔 수 있는 경계(구간 시작/끝 + 시간 양끝)만 모아 그 사이사이를 한 조각씩 판정한다
    const boundaries = new Set([hourStart, effectiveEnd]);
    [...stateIntervals, ...alarmOnIntervals].forEach((iv) => {
      if (iv.st > hourStart && iv.st < effectiveEnd) boundaries.add(iv.st);
      if (iv.ed > hourStart && iv.ed < effectiveEnd) boundaries.add(iv.ed);
    });
    const points = Array.from(boundaries).sort((a, b) => a - b);

    for (let i = 0; i < points.length - 1; i += 1) {
      const segSt = points[i];
      const segEd = points[i + 1];
      const segMin = (segEd - segSt) / MIN_MS;
      if (segMin <= 0) continue;
      const midMs = segSt + (segEd - segSt) / 2; // 경계값 자체는 모호하니 조각 가운데로 판정
      const stVal = stateValAt(midMs);
      let key;
      if (stVal === 'EMERGENCY') key = 'EMERGENCY';
      else if (alarmOnAt(midMs)) key = 'ALARM';
      else if (stVal) key = stVal;
      else key = 'NODATA';
      bucket[key] = (bucket[key] || 0) + segMin;
    }
  }
  return hours;
};

// 그 시간의 가동률(%) = 운전 분 / (지나간 분 - 오프라인 - 데이터없음). 오프라인·데이터없음은
// "장비가 뭘 했는지 모른다"라 가동/비가동 어느 쪽에도 안 넣는다 - EqpDayTimeline 칩·board 카드와 같은 공식.
// 분모가 0 이하(그 시간이 통째로 오프라인/데이터없음)면 null(가동률 0%가 아니라 "모름").
export const hourlyUtilPct = (bucket) => {
  if (!bucket || bucket.elapsedMin <= 0) return null;
  const denom = bucket.elapsedMin - (bucket.OFFLINE || 0) - (bucket.NODATA || 0);
  return denom > 0 ? (bucket.RUNNING / denom) * 100 : null;
};

/**
 * partCnt(기계 누적 카운터, 리셋될 수 있음)의 분 단위 증가분을 시간대별로 합산한다.
 *  - cur >= prev(직전 유효값) : delta = cur - prev (정상 누적)
 *  - cur <  prev              : 카운터 리셋으로 보고 delta = cur (리셋 이후 누적분 그대로)
 *  - null/undefined           : 그 분만 건너뛴다 - 직전 유효값(baseline)은 그대로 둔다.
 *    (결측 한 건 때문에 다음 정상값을 "리셋"으로 잘못 보면 안 되므로)
 *  - 하루의 첫 유효값은 "시작점"일 뿐이라 delta 계산에 쓰지 않는다(baseline 으로만 삼음).
 *  - delta 는 그 분(현재 분, ts)이 속한 시(hour)에 더한다.
 */
export const computeHourlyPartCounts = (metrics) => {
  const hourly = Array.from({ length: 24 }, (_, hour) => ({ hour, partCnt: 0 }));
  let baseline = null;
  (metrics || []).forEach((m) => {
    const cur = m.partCnt;
    if (cur === null || cur === undefined) return; // 결측 - baseline 유지하고 건너뜀
    if (baseline === null) { baseline = cur; return; } // 하루 첫 유효값 = 시작점(가공수 아님)
    const delta = cur >= baseline ? cur - baseline : cur;
    baseline = cur;
    if (delta > 0) {
      const h = dayjs(m.ts).hour();
      if (hourly[h]) hourly[h].partCnt += delta;
    }
  });
  return hourly;
};

export const totalPartCount = (hourlyParts) => (hourlyParts || []).reduce((s, h) => s + (h.partCnt || 0), 0);

/**
 * 시간대별 차트에 보여줄 시(hour) 범위.
 *  - 오늘이 아니면(지난 날짜) 00~23 전체.
 *  - 오늘이면 STATE 구간이나 분 단위 데이터가 "처음" 나타난 시부터 지금 시까지.
 *    데이터가 전혀 없으면 0시부터. 폭이 3시간 미만이면 뒤로 당겨 최소 3시간을 보장한다
 *    (현재 시각보다 뒤로는 못 당기므로 항상 startHour 쪽을 줄인다).
 */
export const computeZoomHourRange = ({ isToday, intervals = [], metrics = [], now }) => {
  if (!isToday) return { startHour: 0, endHour: 23 };

  const nowHour = dayjs(now).hour();
  const candidateHours = [];
  (intervals || []).forEach((iv) => { if (iv.stDt) candidateHours.push(dayjs(iv.stDt).hour()); });
  (metrics || []).forEach((m) => { if (m.ts) candidateHours.push(dayjs(m.ts).hour()); });

  let startHour = candidateHours.length > 0 ? Math.min(...candidateHours) : 0;
  startHour = Math.min(startHour, nowHour); // 혹시 섞인 미래 시각이 있어도 지금을 넘기지 않음
  const endHour = Math.max(nowHour, startHour);

  const width = endHour - startHour + 1;
  if (width < 3) startHour = Math.max(0, endHour - 2); // 최소 3시간 폭 보장(0시 아래로는 못 내림)

  return { startHour, endHour };
};
